// @givmo/api-client — PII-safe network logging.
//
// Invariant carried VERBATIM from the Flutter stack
// (givmo-webapp/lib/core/interceptors/{request_logging,error_logger}_interceptor.dart
// and givmo-payment-repository's `Failure.toString`): network logs carry
// METADATA ONLY — method, path, status, correlation id, error class. NEVER:
//   - request or response BODIES (donation amounts, payment-method tokens,
//     Apple/Google-Pay tokens, company-admin / donor emails),
//   - the `Authorization` / `X-Firebase-AppCheck` header values,
//   - any server-derived error string that may embed PII.
// These land in browser consoles, screen recordings, and any future Sentry/SDK
// that captures console output. A log line is structurally incapable of leaking
// PII only if it never receives a body in the first place — so the logger's
// type signature refuses bodies, rather than trusting callers to redact.

import type { RequestIdPrefix } from './correlation';

/** Metadata permitted in a network log line. Bodies/headers are intentionally absent. */
export interface NetworkLogMeta {
  readonly phase: 'request' | 'response' | 'error';
  readonly method: string;
  /** Path only — never the full URL with a query string (queries can carry ids). */
  readonly path: string;
  readonly status?: number | 'no-response';
  readonly requestId?: string | 'missing';
  /** Stable error class name (e.g. `'GivmoNetworkError'`) — never the message. */
  readonly errorKind?: string;
}

export interface NetworkLogger {
  log(meta: NetworkLogMeta): void;
}

/** A logger that drops everything — the default in deployed environments. */
export const noopLogger: NetworkLogger = { log() {} };

/**
 * A `console`-backed logger gated behind an explicit enable flag. The host wires
 * `enabled: !isProduction` so structured diagnostics show in dev consoles but
 * never in prod (where console output is captured by monitoring SDKs).
 *
 * Even when enabled it only ever stringifies {@link NetworkLogMeta}, so there is
 * no path by which a body or token reaches the sink.
 */
export function createConsoleLogger(prefix: RequestIdPrefix, enabled: boolean): NetworkLogger {
  if (!enabled) return noopLogger;
  return {
    log(meta) {
      const status = meta.status ?? '';
      const id = meta.requestId ?? 'missing';
      const kind = meta.errorKind ? ` kind=${meta.errorKind}` : '';
      console.debug(
        `[${prefix}.network ${meta.phase} ${meta.method} ${meta.path}] status=${status} id=${id}${kind}`,
      );
    },
  };
}

/**
 * Extract a log-safe path from a URL. Strips the query string and fragment so
 * id-bearing query params (`?charityId=…`, `?report_id=…`) never reach a log.
 */
export function logSafePath(url: string): string {
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    return parsed.pathname;
  } catch {
    // Not a parseable URL — fall back to the substring before any `?`.
    const q = url.indexOf('?');
    return q === -1 ? url : url.slice(0, q);
  }
}
